// src/pages/VibeLifeDesign.jsx - Enhanced Promotional Landing Page
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, ArrowRight, Sparkles, Crown, Zap, Target, Star, Clock, Brain, Telescope, Building, Waves, Eye, 
  PlayCircle, TrendingUp, Heart, Trophy, Gamepad2, Shield, Lock, Users, CheckCircle, AlertTriangle,
  Coffee, Lightbulb, Calculator, Home, Flame, Award, BookOpen, Wrench, Settings, Globe, ChevronDown,
  Timer, GraduationCap, Rocket, Gift, Handshake, TreeDeciduous, Sun, Feather, Code, Megaphone,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { usePrivacyAuth } from '../context/PrivacyAuthContext';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';

const VibeLifeDesign = () => {
  const { setCurrentView, completedModules = [] } = useApp();
  const { user } = usePrivacyAuth();
  const [showFullModules, setShowFullModules] = useState(false);
  const [showFullTools, setShowFullTools] = useState(false);

  // Foundation readiness check
  const foundationModulesCompleted = completedModules.filter(id => id <= 8).length;
  const isReadyForLifeDesign = foundationModulesCompleted >= 6;
  const lifeDesignModulesCompleted = completedModules.filter(id => id >= 9 && id <= 12).length;

  // FOUR Learning Modules - Complete and compelling descriptions
  const lifeDesignModules = [
    {
      id: 9,
      icon: Search,
      title: 'The Opportunity Detective',
      subtitle: 'Uncover what you\'re really trading your time for',
      description: 'Learn to spot the hidden costs of your choices. Spoiler: you\'re not just spending time, you\'re trading opportunities for growth, connections, and your future.',
      duration: '25 minutes',
      difficulty: 'Foundation',
      color: 'from-orange-500 to-red-500',
      badge: 'Detective Mode',
      emoji: '🕵️‍♀️',
      keySkills: ['Hidden cost analysis', 'Opportunity recognition', 'Trade-off awareness'],
      realTalk: 'That hour scrolling TikTok? You just traded potential skill-building time.',
      moduleConnection: 'Builds awareness that leads to conscious choice-making',
    },
    {
      id: 10,
      icon: Telescope,
      title: 'Future Self Advocacy',
      subtitle: 'Make your future self your closest advisor',
      description: 'Connect with the person you\'re becoming and let them guide your daily choices. This isn\'t about sacrifice—it\'s about falling in love with who you\'re growing into.',
      duration: '30 minutes',
      difficulty: 'Intermediate',
      color: 'from-blue-500 to-indigo-500',
      badge: 'Time Traveller',
      emoji: '🔮',
      keySkills: ['Future self visioning', 'Long-term thinking', 'Identity-based decisions'],
      realTalk: 'Your future self is cheering you on. Time to make choices they\'ll thank you for.',
      moduleConnection: 'Transforms how you think about consequences and goals',
    },
    {
      id: 11,
      icon: Building,
      title: 'Conscious Choice Architecture',
      subtitle: 'Design environments that make good choices automatic',
      description: 'Stop relying on willpower. Become the architect of spaces—physical, digital, and social—that naturally guide you toward the life you want to create.',
      duration: '35 minutes',
      difficulty: 'Advanced',
      color: 'from-emerald-500 to-cyan-500',
      badge: 'Environment Hacker',
      emoji: '🏗️',
      keySkills: ['Environment design', 'Habit architecture', 'Friction elimination'],
      realTalk: 'Your phone setup is either helping or sabotaging you. Time to take control.',
      moduleConnection: 'Makes positive choices effortless instead of effortful',
    },
    {
      id: 12,
      icon: Waves,
      title: 'Legacy Building',
      subtitle: 'Transform your growth into positive impact',
      description: 'Level up from improving yourself to improving the world. Learn how your personal development creates ripple effects that influence others in ways you never imagined.',
      duration: '40 minutes',
      difficulty: 'Mastery',
      color: 'from-purple-500 to-pink-500',
      badge: 'Impact Creator',
      emoji: '🌟',
      keySkills: ['Ripple effect awareness', 'Influence psychology', 'Community building'],
      realTalk: 'Your growth doesn\'t happen in a vacuum. Time to multiply your positive impact.',
      moduleConnection: 'Transforms personal development into community transformation',
    },
  ];

  // THREE New Tools - Enhanced descriptions with compelling copy
  const lifeDesignTools = [
    {
      id: 'opportunity-detective-kit',
      toolId: 'time-trade-calculator',
      icon: Calculator,
      title: 'Time Trade Calculator',
      subtitle: 'Reveal the true cost of your choices',
      description: 'Ever wonder what you\'re actually trading when you choose one thing over another? This tool makes invisible opportunity costs visible.',
      detailedDescription: 'Every choice is a trade. That extra hour on social media isn\'t just time—it\'s potential skill-building, relationship-deepening, or creative time. This calculator shows you exactly what you\'re swapping and helps you make trades you\'ll actually be happy with.',
      color: 'from-orange-500 to-red-500',
      badge: 'Reality Check',
      emoji: '🔍',
      coolFactor: 'Like a financial calculator, but for your life choices',
      resultExample: 'Users often discover they\'re trading 10+ hours/week of potential growth time',
      features: ['Hidden cost analysis', 'Opportunity visualisation', 'Trade-off comparisons', 'Future impact projections'],
    },
    {
      id: 'choice-architect-studio',
      toolId: 'environment-design-studio',
      icon: Wrench,
      title: 'Environment Design Studio',
      subtitle: 'Build spaces that automatically support your goals',
      description: 'Your environment is constantly making micro-decisions for you. Time to become the architect of spaces that work for, not against, your future self.',
      detailedDescription: 'Your phone setup, room layout, and social circles are secretly influencing thousands of daily micro-choices. This studio helps you redesign your physical, digital, and social environments so good choices become the easy choices.',
      color: 'from-emerald-500 to-cyan-500',
      badge: 'Space Hacker',
      emoji: '🏗️',
      coolFactor: 'Interior design meets life optimisation',
      resultExample: 'Simple environment changes can double productivity without extra effort',
      features: ['Digital environment audit', 'Physical space optimisation', 'Social circle analysis', 'Habit automation setup'],
    },
    {
      id: 'legacy-impact-tracker',
      toolId: 'impact-ripple-visualiser',
      icon: Waves,
      title: 'Impact Ripple Visualiser',
      subtitle: 'See how your growth influences others',
      description: 'Your positive changes don\'t happen in isolation. This tool reveals the expanding circles of influence you create just by becoming your best self.',
      detailedDescription: 'When you grow, others notice. When you make positive changes, you give others permission to do the same. This visualiser maps the ripple effects of your development and shows how one person\'s growth can influence dozens of lives.',
      color: 'from-purple-500 to-pink-500',
      badge: 'Ripple Creator',
      emoji: '🌊',
      coolFactor: 'Watch your growth become everyone else\'s inspiration',
      resultExample: 'See how one person\'s positive change can influence 100+ people over 5 years',
      features: ['Influence mapping', 'Ripple effect tracking', 'Impact amplification strategies', 'Community building insights'],
    },
  ];

  // Enhanced transformation benefits
  const transformationBenefits = [
    {
      before: 'Making choices on autopilot',
      after: 'Every decision is conscious and intentional',
      impact: '↗ 300% more alignment with your actual values',
      icon: Brain,
      color: 'text-emerald-400',
    },
    {
      before: 'Fighting your environment daily',
      after: 'Your spaces automatically support good choices',
      impact: '↗ 200% easier to maintain positive habits',
      icon: Building,
      color: 'text-blue-400',
    },
    {
      before: 'Growth that only affects you',
      after: 'Positive influence that ripples outward',
      impact: '↗ Unlimited potential for meaningful impact',
      icon: Waves,
      color: 'text-purple-400',
    },
    {
      before: 'Short-term thinking patterns',
      after: 'Future self becomes your closest advisor',
      impact: '↗ 500% better long-term decision making',
      icon: Telescope,
      color: 'text-yellow-400',
    },
  ];

  const handleToolExplore = (toolId) => {
    if (!user) {
      setCurrentView('signup');
      return;
    }
    setCurrentView('tools');
    localStorage.setItem('selectedTool', toolId);
  };

  const handleModulesExplore = () => {
    if (!user) {
      setCurrentView('signup');
      return;
    }
    setCurrentView('modules');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900">
      <div className="container mx-auto px-4 py-8">
        {/* Floating Emojis */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <motion.div
            animate={{ 
              y: [-10, 10, -10], 
              x: [-5, 5, -5],
              rotate: [0, -15, 15, 0],
            }}
            transition={{ duration: 4, repeat: Infinity, delay: 0 }}
            className="absolute top-20 left-20 text-5xl filter drop-shadow-lg"
          >
            🕵️‍♀️
          </motion.div>
          <motion.div
            animate={{ 
              y: [8, -8, 8], 
              rotate: [0, -10, 10, 0],
              scale: [1, 1.05, 1],
            }}
            transition={{ duration: 3.5, repeat: Infinity, delay: 1 }}
            className="absolute top-32 right-16 text-4xl filter drop-shadow-lg"
          >
            🏗️
          </motion.div>
          <motion.div
            animate={{ 
              scale: [1, 1.2, 1],
              rotate: [0, 5, -5, 0],
            }}
            transition={{ duration: 2.5, repeat: Infinity, delay: 2 }}
            className="absolute top-64 left-32 text-3xl filter drop-shadow-lg"
          >
            🌊
          </motion.div>
          <motion.div
            animate={{ 
              y: [-5, 5, -5],
              x: [-2, 2, -2],
            }}
            transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
            className="absolute top-80 right-32 text-2xl filter drop-shadow-lg"
          >
            🔮
          </motion.div>
        </div>

        {/* Hero Section */}
        <motion.div 
          className="text-center mb-16 relative pt-32"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Badge */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <Crown className="w-6 h-6 text-yellow-400" />
            <motion.span 
              className="px-4 py-2 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-full text-emerald-300 font-medium"
              whileHover={{ scale: 1.05 }}
            >
              {user && lifeDesignModulesCompleted > 0 ? 
                `🎯 Life Design Series - ${lifeDesignModulesCompleted}/4 Complete` :
                '🔥 New Advanced Addon - Life Design Series'
              }
            </motion.span>
          </div>
          
          <motion.h1 
            className="text-5xl md:text-7xl font-bold text-white mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Conscious Life Design
          </motion.h1>
          
          {/* The Hook */}
          <motion.div 
            className="max-w-5xl mx-auto space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-3xl md:text-4xl text-emerald-300 font-bold leading-tight">
              Ready to Stop Living on Autopilot? 🤔
            </h2>
            <p className="text-xl md:text-2xl text-slate-300 leading-relaxed font-light">
              Transform from someone who reacts to life to someone who <span className="text-orange-300 font-medium">consciously designs it</span>. 
              Master the art of <span className="text-emerald-300 font-medium">intentional choices</span>, 
              <span className="text-cyan-300 font-medium"> environment optimisation</span>, and 
              <span className="text-purple-300 font-medium"> meaningful impact</span>.
            </p>
            <motion.p 
              className="text-lg text-emerald-300 font-medium flex items-center justify-center gap-2 flex-wrap"
              whileHover={{ scale: 1.02 }}
            >
              <Zap className="w-5 h-5 text-yellow-400" />
              <span className="text-white">Real talk:</span> You're already designing your life with every choice. 
              Why not do it <span className="text-emerald-200 underline decoration-emerald-400">consciously</span>?
            </motion.p>
          </motion.div>

          {/* Readiness Assessment */}
          <motion.div 
            className="mt-10 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            {user ? (
              isReadyForLifeDesign ? (
                <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/40 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="flex items-center justify-center gap-3 text-green-300 mb-3">
                    <CheckCircle className="w-6 h-6" />
                    <span className="font-bold text-lg">🎉 You're Ready for Life Design!</span>
                  </div>
                  <p className="text-green-200">
                    You've completed {foundationModulesCompleted}/8 foundation modules and built the perfect foundation for advanced concepts. 
                    Time to level up your entire approach to life choices!
                  </p>
                </div>
              ) : (
                <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/40 rounded-2xl p-6 backdrop-blur-sm">
                  <div className="flex items-center justify-center gap-3 text-yellow-300 mb-3">
                    <Lightbulb className="w-6 h-6" />
                    <span className="font-bold text-lg">💪 Foundation Building in Progress</span>
                  </div>
                  <p className="text-yellow-200 mb-3">
                    Complete {6 - foundationModulesCompleted} more foundation modules for the optimal Life Design experience. 
                    ({foundationModulesCompleted}/8 done - you're {Math.round((foundationModulesCompleted/8)*100)}% there!)
                  </p>
                  <p className="text-yellow-100 text-sm">
                    <strong>Why wait?</strong> Foundation modules build the mental wellness skills that make Life Design tools even more powerful.
                  </p>
                </div>
              )
            ) : (
              <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/40 rounded-2xl p-6 backdrop-blur-sm">
                <div className="flex items-center justify-center gap-3 text-purple-300 mb-3">
                  <Star className="w-6 h-6" />
                  <span className="font-bold text-lg">✨ Ready to Start Your Journey?</span>
                </div>
                <p className="text-purple-200">
                  Take the Vibe Check to get personalised module recommendations, then dive into Life Design when you're ready!
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>

        {/* FOUR Learning Modules Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <BookOpen className="w-6 h-6 text-emerald-400" />
              <span className="text-emerald-400 font-medium">Four Life-Changing Modules</span>
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">Master Life Design in Four Acts</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Progressive modules that build on each other to transform how you approach choices, environments, and impact.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {lifeDesignModules.slice(0, showFullModules ? 4 : 2).map((module, index) => (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 + index * 0.1 }}
                whileHover={{ y: -5 }}
                className="group"
              >
                <Card className="p-8 h-full bg-gradient-to-br from-slate-800/80 to-slate-700/80 border-slate-600/50 hover:border-slate-500/50 transition-all duration-300">
                  <div className="flex items-start gap-4 mb-6">
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${module.color} flex-shrink-0`}>
                      <module.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{module.emoji}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${module.color} text-white`}>
                          {module.badge}
                        </span>
                        <span className="text-slate-400 text-sm">Module {module.id}</span>
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">{module.title}</h3>
                      <p className="text-emerald-300 font-medium mb-3">{module.subtitle}</p>
                    </div>
                  </div>
                  
                  <p className="text-slate-300 mb-4 leading-relaxed">{module.description}</p>
                  
                  <div className="bg-slate-700/50 rounded-lg p-3 mb-4">
                    <p className="text-yellow-200 text-sm font-medium italic">"{module.realTalk}"</p>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mb-4">
                    {module.keySkills.map((skill, i) => (
                      <span key={i} className="px-2 py-1 bg-slate-600/50 rounded text-slate-300 text-xs">
                        {skill}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-slate-400">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{module.duration}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      <span>{module.difficulty}</span>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {!showFullModules && (
            <div className="text-center">
              <Button
                onClick={() => setShowFullModules(true)}
                variant="ghost"
                className="text-emerald-400 hover:text-emerald-300"
              >
                <ChevronDown className="w-4 h-4 mr-2" />
                See All Four Modules
              </Button>
            </div>
          )}
        </motion.div>

        {/* THREE New Tools Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Wrench className="w-6 h-6 text-cyan-400" />
              <span className="text-cyan-400 font-medium">Three Powerful Tools</span>
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">Your Life Design Toolkit</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Interactive tools that transform learning into action. Practice your new skills in a safe space before applying them to real life.
            </p>
          </div>

          <div className="grid md:grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
            {lifeDesignTools.slice(0, showFullTools ? 3 : 1).map((tool, index) => (
              <motion.div
                key={tool.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 + index * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="group cursor-pointer"
                onClick={() => handleToolExplore(tool.toolId)}
              >
                <Card className="p-8 h-full bg-gradient-to-br from-slate-800/80 to-slate-700/80 border-slate-600/50 hover:border-cyan-400/50 transition-all duration-300 group-hover:shadow-xl group-hover:shadow-cyan-400/20">
                  <div className="text-center mb-6">
                    <div className="relative inline-block mb-4">
                      <div className={`p-4 rounded-2xl bg-gradient-to-br ${tool.color} inline-block`}>
                        <tool.icon className="w-8 h-8 text-white" />
                      </div>
                      <div className="absolute -top-2 -right-2 text-2xl">{tool.emoji}</div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r ${tool.color} text-white`}>
                      {tool.badge}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-2 text-center">{tool.title}</h3>
                  <p className="text-cyan-300 font-medium mb-4 text-center">{tool.subtitle}</p>
                  <p className="text-slate-300 mb-4 leading-relaxed text-center">{tool.description}</p>
                  
                  <div className="bg-slate-700/50 rounded-lg p-3 mb-4">
                    <p className="text-cyan-200 text-sm italic text-center">"{tool.coolFactor}"</p>
                  </div>
                  
                  <div className="space-y-2 mb-6">
                    {tool.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
                        <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></div>
                        {feature}
                      </div>
                    ))}
                  </div>
                  
                  <Button
                    className={`w-full bg-gradient-to-r ${tool.color} hover:opacity-90`}
                    size="sm"
                  >
                    <PlayCircle className="w-4 h-4 mr-2" />
                    Try This Tool
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>

          {!showFullTools && (
            <div className="text-center">
              <Button
                onClick={() => setShowFullTools(true)}
                variant="ghost"
                className="text-cyan-400 hover:text-cyan-300"
              >
                <ChevronDown className="w-4 h-4 mr-2" />
                See All Three Tools
              </Button>
            </div>
          )}
        </motion.div>

        {/* Transformation Benefits */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">The Transformation</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Here's what changes when you master conscious life design.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {transformationBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.3 + index * 0.1 }}
                className="group"
              >
                <Card className="p-6 bg-gradient-to-br from-slate-800/80 to-slate-700/80 border-slate-600/50 hover:border-slate-500/50 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <benefit.icon className={`w-8 h-8 ${benefit.color} flex-shrink-0 mt-1`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-red-400 text-sm">BEFORE:</span>
                        <span className="text-slate-400">{benefit.before}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-green-400 text-sm">AFTER:</span>
                        <span className="text-white font-medium">{benefit.after}</span>
                      </div>
                      <div className={`text-sm font-bold ${benefit.color}`}>
                        {benefit.impact}
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Beta Access CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4 }}
          className="text-center"
        >
          <Card className="p-12 bg-gradient-to-br from-purple-900/50 to-pink-900/50 border-purple-500/30">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-center gap-2 mb-6">
                <Crown className="w-8 h-8 text-yellow-400" />
                <span className="px-4 py-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-full text-yellow-300 font-bold text-lg">
                  🎉 Free Beta Access
                </span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Ready to Design Your Life Consciously?
              </h2>
              
              <p className="text-xl text-purple-200 mb-8 leading-relaxed">
                Join thousands of young adults who are transforming from reactive to conscious. 
                <span className="text-yellow-300 font-semibold"> Life Design is free during beta testing</span>, 
                but this is limited-time access before it becomes a premium addon.
              </p>
              
              <div className="space-y-4">
                <Button
                  onClick={() => user ? handleModulesExplore() : setCurrentView('vibe-assessment-intro')}
                  size="lg"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-lg px-8 py-4"
                >
                  <Rocket className="w-6 h-6 mr-3" />
                  {user ? 'Start Life Design Modules' : 'Take Free Vibe Check First'}
                </Button>
                
                <div className="flex items-center justify-center gap-6 text-sm text-purple-300">
                  <div className="flex items-center gap-2">
                    <Timer className="w-4 h-4" />
                    <span>2.5 hours total content</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    <span>4 completion badges</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <span>100% privacy-first</span>
                  </div>
                </div>
              </div>
              
              <p className="text-slate-400 text-sm mt-6">
                No credit card required during beta. Full access to all modules and tools.
              </p>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default VibeLifeDesign;